/* Card database for the browser.
 *
 * Fetched at runtime from punk-records (generated from Bandai's official list)
 * so new sets appear without a release here. One entry per card NUMBER: the raw
 * dump lists every printing separately (OP15-061, OP15-061_p1, …) and those are
 * the same card for deckbuilding — the extra printings are also exactly where
 * the missing-art gaps came from, so base printings win.
 *
 * "New sets appear without a release here" holds only once punk-records has
 * them, which is weeks after a set is announced and playable. `data/sets/`
 * covers that window — see src/data/setfile.js.
 */

import { normalizeSetFile, mergeSets, isProvisional } from './setfile.js';

const CDN = 'https://cdn.jsdelivr.net/gh/buhbbl/punk-records@main/english';
const LOCAL_SETS = './data/sets';

export const COLORS = ['Red', 'Green', 'Blue', 'Purple', 'Black', 'Yellow'];
export const CATEGORIES = ['Leader', 'Character', 'Event', 'Stage'];

/** colour name -> CSS custom property suffix used by the design tokens */
export const colorVar = c => `--c-${String(c).toLowerCase()}`;
export const tintVar = c => `--t-${String(c).toLowerCase()}`;

/* One stable object for the lifetime of the page, so a reference taken before
 * the load resolves still sees the cards afterwards.
 *
 * Treat it as read-only. Callers that need to stand in for ids the dump lacks
 * go through `withStubs` — see the note there for why. */
const db = {};
let loaded = false;
let loading = null;

/* Sets the last load asked for and never got. Empty is the normal answer.
 *
 * A set file that fails to arrive used to be indistinguishable from a set with
 * no cards in it: the fetch swallowed the error and returned `[]`, so the app
 * carried on a few hundred cards short and said nothing. That is survivable in
 * the builder — you notice the search is missing something — and it is not
 * survivable in `npm run scenarios`, where a puzzle referencing a card from
 * the set that went missing reports as *unsolvable*. A gating job must not
 * blame the content for the network. */
let unavailable = [];
export const setsUnavailable = () => [...unavailable];

export function getCards() { return db; }
export function isLoaded() { return loaded; }

/** Seed the database directly — used by tests and by offline fixtures. */
export function setCards(entries) {
  Object.assign(db, entries);
  loaded = true;
  return db;
}

/**
 * @param {(done:number,total:number)=>void} [onProgress]
 * @returns {Promise<Record<string, object>>}
 */
export function loadCards(onProgress) {
  if (loaded) return Promise.resolve(db);
  if (loading) return loading;

  loading = (async () => {
    const packs = await fetch(`${CDN}/packs.json`).then(r => {
      if (!r.ok) throw new Error(`card index unavailable (HTTP ${r.status})`);
      return r.json();
    });
    const ids = Object.keys(packs);
    let done = 0;
    unavailable = [];

    const sets = await Promise.all(ids.map(async id => {
      const cards = await fetchSet(id);
      if (cards === null) unavailable.push(id);
      onProgress?.(++done, ids.length);
      return cards ?? [];
    }));

    const raw = sets.flat();
    for (const c of raw) if (!c.id.includes('_') && !db[c.id]) db[c.id] = c;
    for (const c of raw) {
      const base = c.id.split('_')[0];
      if (!db[base]) db[base] = { ...c, id: base, _variant: c.id };
    }

    mergeSets(db, await loadLocalSets());

    loaded = true;
    return db;
  })();

  /* Clear the memo on failure so a retry can actually retry.
   *
   * The rejected promise used to stay in `loading`, and every later
   * loadCards() returned that same rejection — one flaky network moment and
   * the page could never recover without a full reload. */
  loading.catch(() => { loading = null; });

  return loading;
}

/**
 * One set file, retried.
 *
 * Every pack is fetched at once and the CDN rate-limits, which from a CI
 * runner is a 429 on whichever request is unlucky. One dropped set silently
 * cost the database its cards — that is how `blockers-must-be-active` failed
 * CI as "the card database is missing ST11-002" while passing everywhere else.
 *
 * A 404 is an answer, not a failure: the index lists packs whose file is not
 * published yet, and retrying will not publish it.
 *
 * @returns {Promise<object[]|null>} the cards, or null if we gave up
 */
async function fetchSet(id, attempts = 3) {
  for (let n = 1; n <= attempts; n++) {
    try {
      const r = await fetch(`${CDN}/data/${id}.json`);
      if (r.ok) return await r.json();
      if (r.status === 404) return [];
    } catch { /* network, not an answer — worth another go */ }
    if (n < attempts) await new Promise(res => setTimeout(res, 250 * n));
  }
  return null;
}

/**
 * Local set supplements, for sets the upstream dump doesn't carry yet.
 *
 * Every failure here is non-fatal. The whole app works without local sets —
 * they are additive by definition — so a missing manifest, a 404 or a malformed
 * file costs you the new set and nothing else. Losing the entire card database
 * because a supplement was bad would be a far worse trade.
 */
async function loadLocalSets() {
  let names;
  try {
    const r = await fetch(`${LOCAL_SETS}/index.json`);
    if (!r.ok) return [];
    names = (await r.json())?.sets;
    if (!Array.isArray(names)) return [];
  } catch {
    return [];
  }

  const files = await Promise.all(names.map(async name => {
    try {
      const r = await fetch(`${LOCAL_SETS}/${name}.json`);
      if (!r.ok) return null;
      return normalizeSetFile(await r.json());
    } catch (err) {
      console.warn(`Ignoring local set ${name}: ${err.message}`);
      return null;
    }
  }));
  return files.filter(Boolean);
}

/** Is this card missing printed data the rules need? Re-exported for the UI. */
export { isProvisional };

/* ---------------------------------------------------------------------- */
/* stubs                                                                   */
/* ---------------------------------------------------------------------- */

/** A vanilla body to stand in for an id the card dump doesn't have. */
const stubCard = id => ({
  id, name: `${id} (unknown)`, category: 'Character', colors: ['Black'],
  cost: 4, power: 5000, counter: 1000, effect: '-', trigger: null, types: [],
});

/**
 * A card map that reads through to the database but keeps writes to itself.
 *
 * A decklist can name an id the dump lacks — a brand-new set, a printing that
 * never got an entry — and the engine can't start a game without a card object,
 * so those ids get stubbed as vanilla bodies. Stubbing them *into* `db` leaked
 * them into every other view: after one gauntlet run the builder pool, the
 * hover preview, the card inspector and deck validation all saw a 4-cost 5000
 * Black Character named "<id> (unknown)", and legality was then checked against
 * that fiction.
 *
 * The overlay is a prototype-chain view, which works because every consumer
 * reads by key (`cards[id]`) and none enumerates the map. Real cards resolve
 * through the prototype; stubs are own properties that die with the view. If a
 * caller ever needs `Object.keys` over this, it needs a real copy instead.
 *
 * @param {string[]} ids  ids the caller is about to hand to the engine
 * @returns {{cards: Record<string, object>, missing: string[]}}
 */
export function withStubs(ids) {
  const cards = Object.create(db);
  const missing = [];
  for (const id of ids) {
    if (!id || cards[id]) continue;
    missing.push(id);
    cards[id] = stubCard(id);
  }
  return { cards, missing };
}

/**
 * A plain, own-property copy of a stub view, for sending to a Worker.
 *
 * `withStubs` returns a prototype-chain view, and `structuredClone` — which is
 * what postMessage uses — copies own properties only. Posting a view directly
 * would silently deliver *just the stubs* and drop the entire real database,
 * so every card would come out undefined on the other side.
 *
 * That failure is quiet and total, which is exactly why this is a named
 * function with a comment rather than a spread at the call site.
 */
export function flattenCards(view) {
  return { ...db, ...view };
}

/* ---------------------------------------------------------------------- */
/* images                                                                  */
/* ---------------------------------------------------------------------- */

/**
 * Candidate art URLs, best first. Any single host 404s on some printing —
 * alt arts, promos, brand-new sets — so the tile walks this list on error and
 * falls back to the designed text face rather than showing a blank.
 */
export function imageSources(c) {
  if (!c) return [];
  const out = [];
  const set = String(c.id).split('-')[0];
  const push = u => { if (u && !out.includes(u)) out.push(u); };

  push(`https://limitlesstcg.nyc3.cdn.digitaloceanspaces.com/one-piece/${set}/${c.id}_EN.webp`);
  push(`https://limitlesstcg.nyc3.cdn.digitaloceanspaces.com/one-piece/${set}/${c.id}_JP.webp`);
  if (c._variant) {
    const vset = String(c._variant).split('-')[0];
    push(`https://limitlesstcg.nyc3.cdn.digitaloceanspaces.com/one-piece/${vset}/${c._variant}_EN.webp`);
  }
  push(c.img_full_url);
  return out;
}

/* ---------------------------------------------------------------------- */
/* queries                                                                 */
/* ---------------------------------------------------------------------- */

export const setCodeOf = id => String(id).split('-')[0];

/**
 * Every set code present in the database, newest-looking first.
 *
 * "-looking" is the honest word: the dump carries no release date, so this is
 * a descending sort of the code and nothing more. It puts OP17 above OP01,
 * which is the order a set filter is actually used in — you reach for the
 * filter because a new set landed. The comment used to promise this while the
 * plain ascending sort underneath led with OP01.
 */
export function allSets() {
  const s = new Set(Object.keys(getCards()).map(setCodeOf));
  return [...s].sort().reverse();
}

/** Does this card share a colour with the leader? */
export function matchesLeader(card, leader) {
  if (!leader) return true;
  const lc = leader.colors || [];
  return (card.colors || []).some(c => lc.includes(c));
}

/**
 * Filter the pool.
 * @param {{query,colors,cost,category,set,leader,legalOnly}} f
 */
export function searchCards(f = {}) {
  const q = (f.query || '').trim().toLowerCase();
  const colors = f.colors instanceof Set ? f.colors : new Set(f.colors || []);
  let list = Object.values(getCards());

  if (f.category) list = list.filter(c => c.category === f.category);
  if (f.set) list = list.filter(c => setCodeOf(c.id) === f.set);
  if (f.cost != null && f.cost !== '') {
    const n = +f.cost;
    list = list.filter(c => (n >= 10 ? (c.cost ?? 0) >= 10 : c.cost === n));
  }
  if (colors.size) list = list.filter(c => (c.colors || []).some(x => colors.has(x)));
  if (f.legalOnly && f.leader) {
    /* Once a Leader is chosen the job is filling 50 slots, so other Leaders are
     * noise — there are 136 of them and they were burying the whole pool.
     * They stay reachable through the Leader type filter or a direct search. */
    const browsingLeaders = f.category === 'Leader' || /leader/i.test(q);
    list = list.filter(c => (c.category === 'Leader'
      ? browsingLeaders || c.id === f.leader.id
      : matchesLeader(c, f.leader)));
  }
  if (q) {
    list = list.filter(c =>
      `${c.name} ${c.id} ${c.effect || ''} ${(c.types || []).join(' ')} ${c.trigger || ''}`
        .toLowerCase().includes(q));
  }

  // Leaders first only while you still need one; after that, cards come first.
  const leadersFirst = !f.leader;
  return list.sort((a, b) => {
    const al = a.category === 'Leader', bl = b.category === 'Leader';
    if (al !== bl) return (leadersFirst ? al : bl) ? -1 : 1;
    return a.id.localeCompare(b.id);
  });
}
